/**
 * pinLockLogic.js — pure, React-free decision logic for the PIN session lock.
 *
 * Extracted from PinLockContext.jsx so the cross-tab behaviour is unit-testable
 * (see test/pinLockLogic.test.js + test/pinLockMultiTab.test.js). The live
 * component delegates its three decisions to these functions.
 *
 * The cross-tab bug came down to one missing idea: there was only ever a
 * PER-TAB activity stamp (`sessionStorage`), so no tab could tell that the user
 * was active in another tab. We add a SHARED stamp (`localStorage`) written on
 * every unlock/activity, and every guard consults the newest of the two.
 */

export const INACTIVITY_MS   = 15 * 60 * 1000; // 15 minutes
export const HIDDEN_GRACE_MS  = 30 * 1000;      // lock a tab hidden longer than 30s
export const BROADCAST_CHANNEL = 'ae-pin-lock';

export const SESSION_KEY_LAST_ACTIVE = 'ae:pin:lastActive';        // PER-TAB  (sessionStorage)
export const SHARED_KEY_LAST_ACTIVE  = 'ae:pin:lastActiveShared';  // SHARED   (localStorage)

/** Newest "user was active" timestamp across this tab and any tab. */
export function newestActive(perTabLastActive, sharedLastActive) {
  return Math.max(Number(perTabLastActive || 0), Number(sharedLastActive || 0));
}

/**
 * Should the session auto-lock for inactivity? Consults the SHARED stamp too, so
 * a user active in ANY tab keeps every tab alive — this is what stops an idle
 * background tab's own timer from locking everyone. No stamp at all → fresh.
 */
export function isInactive(now, perTabLastActive, sharedLastActive, inactivityMs = INACTIVITY_MS) {
  const stamp = newestActive(perTabLastActive, sharedLastActive);
  if (!stamp) return false;
  return now - stamp > inactivityMs;
}

/**
 * Should a tab becoming visible again re-lock? Suppressed when a cross-tab
 * unlock/activity landed AFTER this tab went hidden (the user is demonstrably
 * present), which kills the stale-`hiddenSince` snap-back.
 */
export function shouldLockOnVisibilityRestore(hiddenSince, now, sharedLastActive, hiddenGraceMs = HIDDEN_GRACE_MS) {
  if (!hiddenSince) return false;
  if (Number(sharedLastActive || 0) >= hiddenSince) return false;
  return now - hiddenSince > hiddenGraceMs;
}

/**
 * Resulting stage for a received BroadcastChannel message, or null for no change.
 */
export function applyBroadcastTransition(stage, type) {
  if (type === 'lock' && stage === 'unlocked') return 'locked';
  if (type === 'unlock' && stage === 'locked') return 'unlocked';
  return null;
}

// ── Per-user auto-lock duration (user setting, Supervisor + SuperAdmin) ───────
//
// `ms` is the inactivity window; `0` is the sentinel for "Never" (auto-lock
// off), offered to SuperAdmin only. The allowed set is mirrored server-side in
// api/src/lib/pin.js (allowedLockValues) — keep the two in step.
export const LOCK_NEVER = 0;
export const LOCK_DURATION_OPTIONS = [
  { ms: 5  * 60 * 1000,      label: '5 minutes'  },
  { ms: 15 * 60 * 1000,      label: '15 minutes' },
  { ms: 30 * 60 * 1000,      label: '30 minutes' },
  { ms: 60 * 60 * 1000,      label: '60 minutes' },
  { ms: 2  * 60 * 60 * 1000, label: '2 hours', superAdminOnly: true },
  { ms: 4  * 60 * 60 * 1000, label: '4 hours', superAdminOnly: true },
  { ms: LOCK_NEVER,          label: 'Never',   superAdminOnly: true },
];

/** The options a given role may choose from. */
export function lockDurationOptionsFor(isSuperAdmin) {
  return LOCK_DURATION_OPTIONS.filter(o => isSuperAdmin || !o.superAdminOnly);
}

/**
 * Resolve the effective lock behaviour from the role defaults and the user's
 * own preference. Pure so it can be unit-tested and reused by the initial
 * status fetch (which must decide the entry stage before React state settles).
 *
 *   prefMs === null / undefined → no user preference → legacy role behaviour
 *                                  (SuperAdmin never auto-locks; others do).
 *   prefMs === 0                → explicit "Never" → auto-lock off.
 *   prefMs  >  0                → opt into that window (SuperAdmin included).
 *
 * `roleExempt` (role flagged exempt) and a non-required role always win: no
 * auto-lock regardless of preference.
 */
export function resolveLock({
  required,
  isSuperAdmin,
  roleExempt,
  roleInactivityMs,
  roleHiddenGraceMs,
  prefMs,
}) {
  const hasWindow    = typeof prefMs === 'number' && prefMs > 0;
  const inactivityMs = hasWindow ? prefMs : (roleInactivityMs || INACTIVITY_MS);
  const hiddenGraceMs = roleHiddenGraceMs || HIDDEN_GRACE_MS;

  let autoLock;
  if (!required || roleExempt) autoLock = false;
  else if (prefMs === 0)       autoLock = false;         // explicit "Never"
  else if (hasWindow)          autoLock = true;          // opted into a window
  else                         autoLock = !isSuperAdmin; // no pref → legacy default

  return { autoLock, inactivityMs, hiddenGraceMs };
}
